<template>
  <v-container>
    <ObjectMapEntry
        v-for="(item, key) in internalValue"
        :key="key"
        v-bind:keyEntry="key"
        v-bind:valueEntry="item"
        v-bind:property="property"
        v-bind:unusedKeyItems="unusedKeyItems"
        :languageRestrictions="languageRestrictions"
    ></ObjectMapEntry>
    <v-row>
      <v-col v-if="keyIsLabel">
        <v-select
            :label="$t('parameters.label.labelName')"
            :items="unusedLabels"
            item-text="label"
            item-value="name"
            v-model="keyEntry"
            @change="maybeAddMapEntry"
            ref="labelKeySelect"
        ></v-select>
      </v-col>
      <v-col v-else-if="keyIsLexicon">
        <v-select
            v-model="keyEntry"
            :items="unusedLexicons"
            item-text="label"
            item-value="name"
            :label="$t('parameters.label.lexicon')"
            @change="maybeAddMapEntry"
            ref="lexiconKeySelect"
        ></v-select>
      </v-col>
      <v-col v-else-if="keyIsLanguage">
        <v-select
            :label="$t('parameters.label.language')"
            :items="unusedLanguages"
            v-model="keyEntry"
            single-line
            style="width: 300px"
            @change="maybeAddMapEntry"
            ref="languageKeySelect"
        ></v-select>
      </v-col>
      <v-col v-else>
        <v-text-field
            v-model="keyEntry"
            @change="maybeAddMapEntry"
        ></v-text-field>
      </v-col>
      <v-col v-if="valueIsLexicon">
        <v-select
            v-model="value"
            :items="lexicons"
            item-text="label"
            item-value="name"
            :label="$t('parameters.label.lexicon')"
            @change="maybeAddMapEntry"
            ref="lexiconValueSelect"
        ></v-select>
      </v-col>
      <v-col v-else-if="valueIsLanguage">
        <v-select
            :label="$t('parameters.label.language')"
            :items="possibleLanguages"
            v-model="value"
            single-line
            style="width: 300px"
            @change="maybeAddMapEntry"
            ref="languageValueSelect"
        ></v-select>
      </v-col>
      <v-col v-else-if="valueIsJson">
        <v-text-field
            px-5 mx-5
            v-model="value"
            @change="maybeAddMapEntry"
        >
          <template v-slot:append>
            <v-icon
                small
                color="primary"
                @click="jsonEditorDialog = true"
            >
              mdi-pencil
            </v-icon>
          </template>
        </v-text-field>
      </v-col>
      <v-col v-else-if="valueIsEnum">
        <v-select
            :label="property.additionalProperties.default"
            :items="property.additionalProperties.enum"
            v-model="value"
            single-line
            style="width: 300px"
            @change="maybeAddMapEntry"
        ></v-select>
      </v-col>
      <v-col v-else>
        <v-text-field
            color="primary"
            v-model="value"
            @change="maybeAddMapEntry"
        ></v-text-field>
      </v-col>
      <v-col cols="1">
<!--        <v-tooltip top open-delay="600">-->
<!--          <template v-slot:activator="{ on }">-->
<!--            <v-btn-->
<!--                v-on="on"-->
<!--                id="btn_addlabelentry"-->
<!--                icon-->
<!--                text-->
<!--                small-->
<!--                color="primary"-->
<!--                :disabled="keyEntry === null || value === null"-->
<!--                @click="addEntry()"-->
<!--            >-->
<!--              <v-icon>mdi-plus-circle</v-icon>-->
<!--            </v-btn>-->
<!--          </template>-->
<!--          <span>{{ $t("parameters.label.add") }}</span>-->
<!--        </v-tooltip>-->
      </v-col>
    </v-row>
    <v-dialog
        v-model="jsonEditorDialog"
        max-width="640"
        @click:outside="jsonEditorDialog = false"
        @keydown.esc="jsonEditorDialog = false"
    >
      <v-card height="460px">
        <v-card-title class="headline">{{ $t("parameters.label.jsonEditor") }}</v-card-title>
        <v-card-text>
          <v-jsoneditor
              v-model="jsonModel"
              :options="options"
              :plus="false"
              height="320px"
          ></v-jsoneditor>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="jsonEditorDialog = false">
            {{ $t("parameters.label.validate") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ObjectMapEntry from "./ObjectMapEntry";
import {mapState, mapGetters} from "vuex";

export default {
  inject: ["setParamValue", "removeParam"],
  props: ["property", "languageRestrictions"],
  components: {
    ObjectMapEntry,
  },
  provide() {
    return {
      putMapEntry: this.putMapEntry,
      removeMapEntry: this.removeMapEntry,
    };
  },
  data() {
    return {
      options: {
        mode: "code",
        mainMenuBar: true,
        statusBar: false,
      },
      keyEntry: null,
      value: null,
      internalValue: null,
      jsonEditorDialog: false,
    };
  },
  mounted() {
    if (this.property.value) {
      // double parse/stringify is a way to prevent having the same
      // reactive objects at 2 different places (and modifying one modifying the other)
      this.internalValue = JSON.parse(JSON.stringify(this.property.value));
      this.setParamValue(this.property.name, this.property.value);
    } else {
      this.internalValue = {};
    }
    if (
        this.property.additionalProperties &&
        this.property.additionalProperties.format === "json"
    ) {
      this.value = "{}";
    }
  },
  computed: {
    ...mapState("lexicons", ["lexicons"]),
    ...mapGetters("settings", ["extendedLabelsListForSelect"]),
    ...mapGetters(["languages"]),
    possibleLanguages() {
      if (this.property.format !== "language")
        return [];
      if (this.languageRestrictions)
        return this.languages.filter(it => this.languageRestrictions.includes(it.value))
      else
        return this.languages
    },
    unusedLabels() {
      return this.filterUnusedKeyValues(this.extendedLabelsListForSelect);
    },
    unusedLexicons() {
      return this.filterUnusedKeyValues(this.lexicons);
    },
    unusedLanguages() {
      return this.filterUnusedKeyValues(this.possibleLanguages);
    },
    unusedKeyItems() {
      if (this.keyIsLabel) {
        return this.unusedLabels;
      } else if (this.keyIsLexicon) {
        return this.unusedLexicons;
      } else if (this.keyIsLanguage) {
        return this.unusedLanguages;
      } else {
        return [];
      }
    },
    valueIsJson() {
      return (
          this.property.additionalProperties &&
          this.property.additionalProperties.format === "json"
      );
    },
    valueIsEnum() {
      return (
          this.property.additionalProperties &&
          this.property.additionalProperties.enum &&
          this.property.additionalProperties.enum.length > 0
      );
    },
    valueIsLexicon() {
      return (
          this.property.additionalProperties &&
          this.property.additionalProperties.format === "lexicon"
      );
    },
    valueIsLanguage() {
      return (
          this.property.additionalProperties &&
          this.property.additionalProperties.format === "language"
      );
    },
    valueIsLabel() {
      return (
          this.property.additionalProperties &&
          this.property.additionalProperties.format === "label"
      );
    },
    keyIsLexicon() {
      return this.property && this.property.format === "lexicon";
    },
    keyIsLabel() {
      return this.property && this.property.format === "label";
    },
    keyIsLanguage() {
      return this.property && this.property.format === "language";
    },
    jsonModel: {
      get() {
        if (!this.value || !this.isJson) return null;
        return JSON.parse(this.value);
      },
      set(newJsonValue) {
        this.value = JSON.stringify(newJsonValue);
      },
    },
  },
  methods: {
    filterUnusedKeyValues(values) {
      if (this.keyIsLabel || this.keyIsLexicon) {
        return values.filter(
            (it) =>
                !this.internalValue || !this.internalValue.hasOwnProperty(it.name)
        );
      } else if (this.keyIsLanguage) {
        return values.filter(
            (it) =>
                !this.internalValue || !this.internalValue.hasOwnProperty(it.value)
        );
      } else {
        return values.filter(
            (value) =>
                !this.internalValue || !this.internalValue.hasOwnProperty(value)
        );
      }
    },
    maybeAddMapEntry() {
      if (this.keyEntry && this.value)
        this.addEntry();
    },
    addEntry() {
      this.putMapEntry(this.keyEntry, this.value);
      if (this.keyIsLabel) {
        this.$refs.labelKeySelect.reset()
      }
      if (this.keyIsLexicon) {
        this.$refs.lexiconKeySelect.reset()
      }
      if (this.valueIsLexicon) {
        this.$refs.lexiconValueSelect.reset()
      }
      if (this.keyIsLanguage) {
        this.$refs.languageKeySelect.reset()
      }
      if (this.valueIsLanguage) {
        this.$refs.languageValueSelect.reset()
      }
      this.keyEntry = null;
      if (
          this.property.additionalProperties &&
          this.property.additionalProperties.format === "json"
      ) {
        this.value = "{}";
      } else {
        this.value = null;
      }
    },
    putMapEntry(keyEntry, value) {
      if (this.internalValue) {
        // https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats
        this.$set(this.internalValue, keyEntry, value);
        // double parse/stringify is a way to prevent having the same
        // reactive objects at 2 different places (and modifying one modifying the other)
        const newValue = JSON.parse(JSON.stringify(this.internalValue));
        this.setParamValue(this.property.name, newValue);
      } else {
        this.internalValue = {};
        this.internalValue[keyEntry] = value;
        this.setParamValue(this.property.name, this.internalValue);
      }
    },
    removeMapEntry(keyEntry) {
      // https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats
      this.$delete(this.internalValue, keyEntry);
      // double parse/stringify is a way to prevent having the same
      // reactive objects at 2 different places (and modifying one modifying the other)
      const newValue = JSON.parse(JSON.stringify(this.internalValue));
      if (
          Object.keys(newValue).length === 0 &&
          newValue.constructor === Object
      ) {
        this.removeParam(this.property.name);
      } else {
        this.setParamValue(this.property.name, newValue);
      }
    },
  },
};
</script>